import React, { createContext, useContext, useState, useEffect } from "react";
import { Service } from "../@data";
import { getAllServices } from "../services/services";

interface ServicesContextData {
  services: Service[];
  categories: string[];
  loading: boolean;
  fetchServices: () => Promise<void>;
  addCategory: (name: string) => void;
}

const ServicesContext = createContext<ServicesContextData>(
  {} as ServicesContextData
);

export const ServicesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [customCategories, setCustomCategories] = useState<string[]>([]);

  // Extrai categorias únicas dos serviços e combina com categorias customizadas
  const categories = React.useMemo(() => {
    const serviceCategories = new Set(
      services.map((service) => service.category)
    );
    const allCategories = new Set<string>();
    serviceCategories.forEach((cat) => allCategories.add(cat));
    customCategories.forEach((cat) => allCategories.add(cat));
    return Array.from(allCategories).sort();
  }, [services, customCategories]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const servicesData = await getAllServices();
      setServices(servicesData);
    } catch (error) {
      console.error("Erro ao buscar serviços:", error);
    } finally {
      setLoading(false);
    }
  };

  const addCategory = (name: string) => {
    // Adiciona a nova categoria ao array de categorias customizadas se não existir
    if (!customCategories.includes(name)) {
      setCustomCategories((prev) => [...prev, name]);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  return (
    <ServicesContext.Provider
      value={{
        services,
        categories,
        loading,
        fetchServices,
        addCategory,
      }}
    >
      {children}
    </ServicesContext.Provider>
  );
};

export const useServices = () => {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error("useServices deve ser usado dentro de um ServicesProvider");
  }
  return context;
};
